
import React, { useState, useEffect } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { PlusCircle, Trash2 } from "lucide-react";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

type ServiceTemplate = {
  id: string;
  name: string;
  description: string;
  category: string;
};

const serviceSchema = z.object({
  services: z.array(
    z.object({
      template_id: z.string().min(1, "Service type is required"),
      price: z.string().min(1, "Price is required"),
      title: z.string().optional(),
      description: z.string().optional(),
    })
  ).min(1, "Please add at least one service"),
});

const ServicesSetup = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [serviceTemplates, setServiceTemplates] = useState<ServiceTemplate[]>([]);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchServiceTemplates = async () => {
      try {
        const { data, error } = await supabase
          .from('service_templates')
          .select('*')
          .order('name');
          
        if (error) throw error;
        
        setServiceTemplates(data || []);
      } catch (error: any) {
        console.error('Error fetching service templates:', error);
        toast({
          title: "Error loading service templates",
          description: error.message || "There was a problem loading service types",
          variant: "destructive",
        });
      } finally {
        setLoadingData(false);
      }
    };
    
    fetchServiceTemplates();
    
    // Also check if user is logged in
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Authentication required",
          description: "Please sign in to setup your services",
          variant: "destructive",
        });
        navigate("/auth");
      }
    };
    
    checkAuth();
  }, [navigate, toast]);

  const form = useForm({
    resolver: zodResolver(serviceSchema),
    defaultValues: {
      services: [
        { template_id: "", price: "", title: "", description: "" }
      ],
    },
  });
  
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "services"
  });
  
  const getTemplateById = (id: string) => {
    return serviceTemplates.find(template => template.id === id);
  };

  const handleSubmit = async (data: z.infer<typeof serviceSchema>) => {
    setIsLoading(true);
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Authentication error",
          description: "You must be logged in to set up services.",
          variant: "destructive",
        });
        navigate("/auth");
        return;
      }
      
      // Prepare services with template data
      const servicesToInsert = data.services.map(service => {
        const template = getTemplateById(service.template_id);
        return {
          alumni_id: user.id,
          service_template_id: service.template_id,
          title: template?.name || "",
          description: template?.description || "",
          price: parseFloat(service.price),
          is_active: true
        };
      });
      
      // Insert services into the alumni_services table
      const { error } = await supabase
        .from('alumni_services')
        .insert(servicesToInsert);
      
      if (error) {
        throw error;
      }
      
      toast({
        title: "Services added",
        description: "Your services have been added successfully.",
      });
      
      // Navigate to alumni profile
      navigate(`/alumni/${user.id}`);
    } catch (error: any) {
      console.error("Error adding services:", error);
      toast({
        title: "Error adding services",
        description: error.message || "There was an error adding your services",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p>Loading service options...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-10">
      <Header />
      <div className="container max-w-3xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="font-playfair text-3xl font-bold text-crimson">Set Up Your Services</h1>
          <p className="text-gray-600 mt-2">Select the services you'll offer to students and set your pricing</p>
        </div>
        
        <Card className="bg-white">
          <CardHeader>
            <CardTitle>Your Services</CardTitle>
            <CardDescription>
              Select from our predefined service categories and set your pricing
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                {fields.map((field, index) => (
                  <Card key={field.id} className="border-dashed">
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-lg">Service {index + 1}</CardTitle>
                        {index > 0 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => remove(index)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <FormField
                        control={form.control}
                        name={`services.${index}.template_id`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Service Type</FormLabel>
                            <Select 
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select a service type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {serviceTemplates.map((template) => (
                                  <SelectItem key={template.id} value={template.id}>
                                    {template.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {field.value && (
                              <FormDescription>
                                {getTemplateById(field.value)?.description || ""}
                              </FormDescription>
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name={`services.${index}.price`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Price (USD)</FormLabel>
                            <FormControl>
                              <Input 
                                type="number" 
                                min="0" 
                                step="0.01" 
                                placeholder="e.g. 49.99" 
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>
                ))}
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => append({ template_id: "", price: "", title: "", description: "" })}
                  className="w-full border-dashed"
                >
                  <PlusCircle size={16} className="mr-2" />
                  Add Another Service
                </Button>
                
                <div className="pt-4 flex justify-end space-x-4">
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => navigate("/")}
                  >
                    Skip for Now
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-crimson hover:bg-crimson-dark"
                    disabled={isLoading}
                  >
                    {isLoading ? "Saving..." : "Complete Setup"}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default ServicesSetup;
